import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { Reservation } from '@/components/sections/Reservation';
import { SEO } from '@/components/SEO';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/useLocale';

const galleryImages: string[] = [];

const Novita = () => {
  const { t } = useTranslation(['novita', 'seo']);
  const { locale } = useLocale();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const ingredients = t('novita:ingredients', { returnObjects: true }) as Array<{ name: string; desc: string }>;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: t('novita:structuredData.name'),
    description: t('novita:structuredData.description'),
    brand: { '@type': 'Restaurant', name: 'Brace e Sapori' },
    offers: { '@type': 'Offer', price: '38', priceCurrency: 'EUR' },
    inLanguage: locale,
  };

  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title={t('seo:novita.title')}
        description={t('seo:novita.description')}
        keywords={t('seo:novita.keywords')}
        logicalPath="/novita"
        structuredData={structuredData}
      />
      <Header />

      <section className="relative min-h-[70vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/IMG_4308.jpeg"
            alt={t('novita:imageAlt')}
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
        </div>
        <div className="relative z-10 flex min-h-[70vh] flex-col items-center justify-end px-6 pb-20 pt-32 text-center">
          <ScrollReveal>
            <span className="mb-6 inline-block rounded-full bg-amber-700/30 px-6 py-2 text-sm font-bold uppercase tracking-[0.3em] text-amber-300 backdrop-blur-sm">
              {t('novita:badge')}
            </span>
          </ScrollReveal>
          <ScrollReveal delay={150}>
            <h1 className="text-5xl font-extrabold uppercase tracking-tight text-white md:text-7xl lg:text-8xl drop-shadow-lg">
              {t('novita:title')}
            </h1>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-20 px-6 md:py-28">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="mb-12 text-center text-xl font-light italic leading-relaxed text-muted-foreground md:text-2xl">
              {t('novita:tagline')}{' '}
              <span className="font-semibold not-italic text-foreground">{t('novita:taglineBold')}</span>
            </p>
          </ScrollReveal>
          {(['p1', 'p2', 'p3', 'p4'] as const).map((key, i) => (
            <ScrollReveal key={key} delay={100 + i * 100}>
              <div
                className="space-y-6 text-center text-lg font-light leading-relaxed text-muted-foreground [&_strong]:text-foreground"
                dangerouslySetInnerHTML={{ __html: t(`novita:${key}`) }}
              />
            </ScrollReveal>
          ))}
          <ScrollReveal delay={500}>
            <p className="mt-12 text-center text-lg font-light italic text-muted-foreground md:text-xl">{t('novita:closing')}</p>
          </ScrollReveal>
          <ScrollReveal delay={550}>
            <p className="mt-4 text-center text-2xl font-semibold text-foreground md:text-3xl">{t('novita:price')}</p>
          </ScrollReveal>
        </div>
      </section>

      <section className="border-y border-border bg-card py-16 px-6">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="mb-12 text-center text-3xl font-semibold text-foreground md:text-4xl">{t('novita:ingredientsTitle')}</h2>
          </ScrollReveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ingredients.map((ingredient, i) => (
              <ScrollReveal key={ingredient.name} delay={100 + i * 80}>
                <div className="rounded-lg border border-border bg-background p-6 text-center">
                  <h3 className="mb-2 text-lg font-semibold text-foreground">{ingredient.name}</h3>
                  <p className="text-sm font-light text-muted-foreground">{ingredient.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {galleryImages.length > 0 && (
        <section className="py-20 px-6 md:py-28">
          <div className="mx-auto max-w-6xl">
            <ScrollReveal>
              <h2 className="mb-12 text-center text-3xl font-semibold text-foreground md:text-4xl">{t('novita:galleryTitle')}</h2>
            </ScrollReveal>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {galleryImages.map((src, i) => (
                <ScrollReveal key={i} delay={i * 100}>
                  <button onClick={() => setLightboxIndex(i)} className="group relative aspect-square w-full overflow-hidden rounded-lg">
                    <img
                      src={src}
                      alt={t('novita:galleryAlt', { n: i + 1 })}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </button>
                </ScrollReveal>
              ))}
            </div>
          </div>
          <Dialog open={lightboxIndex !== null} onOpenChange={(open) => !open && setLightboxIndex(null)}>
            <DialogContent className="max-w-[95vw] border-0 bg-transparent p-0 shadow-none sm:max-w-[90vw]">
              <DialogTitle className="sr-only">
                {lightboxIndex !== null ? t('novita:galleryAlt', { n: lightboxIndex + 1 }) : ''}
              </DialogTitle>
              {lightboxIndex !== null && (
                <img
                  src={galleryImages[lightboxIndex]}
                  alt={t('novita:galleryAlt', { n: lightboxIndex + 1 })}
                  className="max-h-[90vh] max-w-full rounded-lg object-contain"
                />
              )}
            </DialogContent>
          </Dialog>
        </section>
      )}

      <Reservation />
      <Footer />
    </main>
  );
};

export default Novita;
